import { ClickAwayListener, Stack } from "@mui/material";
import type { DateView } from "@mui/x-date-pickers-pro";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { isAfter, isBefore, isValid } from "date-fns";
import deepEqual from "fast-deep-equal";
import { useState } from "react";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { invalidResult, validResult } from "@entrilia/shared/utilities/validators";
import type { DateComponent, DateEntityFieldConfiguration, FieldAttribute } from "../../../api/types/objectTypes";
import { useFieldValuesContext } from "../FieldValuesContext";
import { findNextEntityInput, findPreviousEntityInput } from "../entityFieldNavigator";
import { formatDateFieldValue } from "../helpers";
import FieldValueWrapper from "./FieldValueWrapper";

interface Props {
  value: Date | null;
  fieldConfiguration: DateEntityFieldConfiguration | undefined;
  fieldAttributes: FieldAttribute[];
  onChange: (newValue: Date | null, validationResult: ValidationResult) => void;
  fieldInputId?: string;
}

interface DateValidationResult extends ValidationResult {
  showValidation?: boolean;
}

const dateComponentToViewMap: Record<DateComponent, DateView> = {
  Day: "day",
  Month: "month",
  Year: "year",
};

const getCalendarHeaderFormat = (fieldConfiguration: DateEntityFieldConfiguration | undefined): string | undefined => {
  if (fieldConfiguration?.dateComponents !== undefined && !fieldConfiguration.dateComponents.includes("Year")) {
    return "MMMM";
  }

  return undefined;
};

const getValidDateRange = (fieldConfiguration: DateEntityFieldConfiguration | undefined): [Date, Date] => {
  if (fieldConfiguration?.dateComponents !== undefined && !fieldConfiguration.dateComponents.includes("Year")) {
    return [new Date(1900, 0, 1), new Date(1900, 11, 31)];
  }

  return [new Date(1900, 0, 1), new Date(2099, 11, 31)];
};

const EntityDateFieldValueComponent = ({
  value,
  fieldConfiguration,
  fieldAttributes,
  onChange,
  fieldInputId,
}: Props) => {
  const [date, setDate] = useState(value);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<DateValidationResult>(validResult);
  const { updateFieldValuesState } = useFieldValuesContext();

  const [minDate, maxDate] = getValidDateRange(fieldConfiguration);

  const handleDateChange = (date: Date | null) => {
    if (date && !isValid(date)) {
      const invalidDateResult = invalidResult("Invalid date");
      setValidationResult(invalidDateResult);
      onChange(null, invalidDateResult);
      return;
    }

    if (fieldAttributes.includes("Required") && date === null) {
      const requiredInvalidResult = invalidResult("Required field. Please select the necessary date.");
      setValidationResult(requiredInvalidResult);
      onChange(null, requiredInvalidResult);
    } else if (date && (isBefore(date, minDate) || isAfter(date, maxDate))) {
      const dateRangeInvalidResult = invalidResult("Date is out of valid range");
      setValidationResult(dateRangeInvalidResult);
      onChange(date, dateRangeInvalidResult);
    } else {
      setValidationResult(validResult());
    }

    setDate(date);
  };

  const [prevValue, setPrevValue] = useState(value);
  if (!deepEqual(value, prevValue)) {
    setPrevValue(value);
    if (value !== undefined) {
      setDate(value || null);
    }
  }

  const handleValueOnLeave = () => {
    if (!validationResult.isValid) {
      setValidationResult({ ...validationResult, showValidation: true });
      return;
    }

    if (!deepEqual(date, value)) {
      onChange(date, validationResult);
    } else {
      updateFieldValuesState({ fieldEditState: "viewing", isValid: true });
    }

    setIsEdit(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleValueOnLeave();
      return;
    }

    if (e.key === "Tab" && fieldInputId) {
      const nextElement = e.shiftKey ? findPreviousEntityInput(fieldInputId) : findNextEntityInput(fieldInputId);
      if (nextElement) {
        e.preventDefault();
        nextElement.click();
      }
      return;
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const openCalendar = document.querySelector(".MuiDateCalendar-root");
    const targetNode = event.target as Node;
    if (openCalendar && targetNode && openCalendar.contains(targetNode)) {
      return;
    }

    handleValueOnLeave();
  };

  return (
    <Stack direction="row">
      {isEdit && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <DatePicker
            autoFocus
            disabled={fieldAttributes.includes("ReadOnly")}
            value={date}
            onChange={handleDateChange}
            views={fieldConfiguration?.dateComponents?.map((component) => dateComponentToViewMap[component])}
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{
              field: {
                clearable: true,
              },
              textField: {
                error: !validationResult.isValid && validationResult.showValidation,
                helperText: validationResult.showValidation ? validationResult.error : "",
                slotProps: { input: { sx: { width: 240 } } },
                onKeyDown: handleKeyDown,
              },
              calendarHeader: {
                format: getCalendarHeaderFormat(fieldConfiguration),
              },
            }}
          />
        </ClickAwayListener>
      )}
      {!isEdit && (
        <FieldValueWrapper
          id={fieldInputId}
          isReadonly={fieldAttributes.includes("ReadOnly")}
          displayValue={formatDateFieldValue(date, fieldConfiguration)}
          isEdit={!validationResult.isValid}
          onEditChange={setIsEdit}
        />
      )}
    </Stack>
  );
};

export default EntityDateFieldValueComponent;
